import React, { useState, useMemo } from "react";
import styled from "styled-components";
import type { StepMaskInfo, MaskKind } from "../utils/regressionEvidence";

const Badge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: #7b1fa2;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    background: #6a1b9a;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #757575;
`;

const MaskTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #e0e0e0;
  }
  th {
    background: #f5f5f5;
    font-weight: 600;
  }
`;

const TypeBadge = styled.span<{ kind: MaskKind }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: ${(p) =>
    p.kind === "cover" ? "#1976d2" : p.kind === "hide" ? "#00796b" : "#c62828"};
`;

export interface MaskBadgeProps {
  masks: StepMaskInfo[];
}

const MASK_ORDER: MaskKind[] = ["cover", "hide", "remove"];

export const MaskBadge: React.FC<MaskBadgeProps> = ({ masks }) => {
  const [open, setOpen] = useState(false);
  const counts = useMemo(() => {
    const c: Record<MaskKind, number> = { cover: 0, hide: 0, remove: 0 };
    for (const m of masks) c[m.maskType]++;
    return c;
  }, [masks]);
  const sorted = useMemo(
    () =>
      [...masks].sort((a, b) => {
        const diff = MASK_ORDER.indexOf(a.maskType) - MASK_ORDER.indexOf(b.maskType);
        if (diff !== 0) return diff;
        return a.name.localeCompare(b.name);
      }),
    [masks]
  );
  if (masks.length === 0) return null;

  const summaryParts: string[] = [];
  MASK_ORDER.forEach((k) => {
    if (counts[k] > 0) summaryParts.push(`${k}:${counts[k]}`);
  });

  return (
    <>
      <Badge onClick={() => setOpen(true)} title="マスク設定の詳細を表示">
        <span role="img" aria-label="mask">🎭</span>
        マスク {masks.length}件 ({summaryParts.join(", ")})
      </Badge>
      {open && (
        <Overlay onClick={() => setOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>マスク設定の詳細</ModalTitle>
              <CloseButton onClick={() => setOpen(false)} aria-label="閉じる">×</CloseButton>
            </ModalHeader>
            <MaskTable>
              <thead>
                <tr>
                  <th>種別</th>
                  <th>要素名</th>
                  <th>説明</th>
                  <th>タグ</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((m) => (
                  <tr key={m.testTargetId}>
                    <td><TypeBadge kind={m.maskType}>{m.maskType}</TypeBadge></td>
                    <td>{m.name}</td>
                    <td>{m.description}</td>
                    <td>{m.maskTags.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </MaskTable>
          </ModalBox>
        </Overlay>
      )}
    </>
  );
};
